import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { Container } from "@/components/design-system/Container";
import { Heading } from "@/components/design-system/Heading";
import { Text } from "@/components/design-system/Text";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert } from "@/components/design-system/Alert";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (resetError) throw resetError;

      toast({
        title: "Email sent",
        description: "Check your email to reset your password",
      });
      
      navigate("/login");
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred while sending the email");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Container size="sm" className="pt-24 px-4">
        <div className="space-y-6">
          <div className="space-y-2 text-center">
            <Heading level="h1">Forgot your password?</Heading>
            <Text size="lg" className="text-muted-foreground">
              Enter your email to receive a password reset link
            </Text>
          </div>

          {error && (
            <Alert variant="error" className="my-4">
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? "Sending..." : "Send reset link"}
            </Button>
          </form>

          <Button
            variant="link"
            className="w-full"
            onClick={() => navigate("/login")}
          >
            Back to login
          </Button>
        </div>
      </Container>
    </div>
  );
};

export default ForgotPassword;